import { useMemo } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { KeycloakRPTSingleton, KeycloakSingleton } from "../components/keycloak-provider";
import { isJWTTokenExpired } from "./auth";

export interface IHttpClient {
  get<T>(path: string, config?: AxiosRequestConfig): Promise<T>;

  post<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res>;

  patch<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res>;

  put<Req, Res>(path: string, body: Req): Promise<Res>;

  delete<Res>(path: string, config?: { [key: string]: any }): Promise<Res>;
}

export class HttpClient implements IHttpClient {
  private readonly requestConfig: AxiosRequestConfig;

  constructor(private baseUrl: string) {
    if (KeycloakRPTSingleton.rpt && isJWTTokenExpired(KeycloakRPTSingleton.rpt)) {
      window.location.reload();
    }
    const accessToken = KeycloakRPTSingleton.rpt ?? KeycloakSingleton?.token;

    this.requestConfig = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };
  }

  async get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.get<T>(fullUrl, { ...this.requestConfig, ...config });
    return response.data;
  }

  async post<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.post<Res>(fullUrl, body, { ...this.requestConfig, ...config });
    return response.data;
  }

  async patch<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.patch<Res>(fullUrl, body, { ...this.requestConfig, ...config });
    return response.data;
  }

  async put<Req, Res>(path: string, body: Req): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.put<Res>(fullUrl, body, this.requestConfig);
    return response.data;
  }

  async delete<Res>(path: string, config?: { [key: string]: any }): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.delete<Res>(fullUrl, { ...this.requestConfig, ...config });
    return response.data;
  }
}

export function useHttpClient(
  baseUrl: string,
  authentication: "property" | "user" | "none" = "none"
): IHttpClient {
  return useMemo(() => new HttpClient(baseUrl), [baseUrl]);
}
